/**
 * Filing – upload documents (no folder) and file them: select document, capture metadata, choose parent folder.
 * Left: document list (75%) + upload zone (25%). Right: metadata (30%) + viewer (70%).
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/shared/PageHeader';
import { useActiveRepositories } from '../../hooks/edms/useActiveRepositories';
import {
  getTemporaryDocuments,
  uploadDocumentTemporary,
  getDocument,
  getDocumentUrl,
  fileDocument,
} from '../../api/edms/documents';
import { getFolderTree, flattenFolderTree } from '../../api/edms/folders';
import { getActiveDocumentTypes } from '../../api/edms/lookups';
import { FileText, Upload, FolderOpen } from 'lucide-react';
import SuccessDialog from '../../components/shared/SuccessDialog';

const APP_ID = 'documents-management';
const APP_NAME = 'Documents Management';
const MODULE = 'Filing';

const GROUP_CATEGORY_OPTIONS = [
  { value: 'Confidential', label: 'Confidential' },
  { value: 'General', label: 'General' },
  { value: 'Personal', label: 'Personal' },
];
const GROUP_SUBCATEGORY_OPTIONS = [
  { value: 'Staff', label: 'Staff' },
  { value: 'Member', label: 'Member' },
];

const EdmsFiling = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [selectedDocId, setSelectedDocId] = useState(null);
  const [filingTitle, setFilingTitle] = useState('');
  const [filingDocumentTypeId, setFilingDocumentTypeId] = useState('');
  const [filingRepository, setFilingRepository] = useState('');
  const [filingGroupCategory, setFilingGroupCategory] = useState('');
  const [filingGroupSubCategory, setFilingGroupSubCategory] = useState('');
  const [filingFolderId, setFilingFolderId] = useState('');
  const [uploadDropActive, setUploadDropActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState({ isOpen: false, message: '' });
  const [fileSuccess, setFileSuccess] = useState({ isOpen: false, message: '' });
  const [filingError, setFilingError] = useState('');

  const { repositories } = useActiveRepositories({ enabled: true });
  const { data: documentTypesRes } = useQuery({
    queryKey: ['edms', 'documentTypes', 'active'],
    queryFn: getActiveDocumentTypes,
  });
  const documentTypes = Array.isArray(documentTypesRes?.data) ? documentTypesRes.data : documentTypesRes ?? [];

  const { data: temporaryData, isLoading: temporaryLoading } = useQuery({
    queryKey: ['edms', 'documents', 'temporary'],
    queryFn: getTemporaryDocuments,
  });
  const unfiledList = useMemo(() => {
    if (!temporaryData) return [];
    if (Array.isArray(temporaryData)) return temporaryData;
    const raw = temporaryData.data ?? temporaryData;
    const list =
      (Array.isArray(raw?.documents) && raw.documents) ??
      (Array.isArray(raw?.data) && raw.data) ??
      (Array.isArray(raw?.items) && raw.items) ??
      (Array.isArray(raw) && raw) ??
      (Array.isArray(temporaryData.documents) && temporaryData.documents) ??
      (Array.isArray(temporaryData.items) && temporaryData.items);
    return list ?? [];
  }, [temporaryData]);

  const filingFolderTreeParams =
    filingRepository && filingGroupCategory
      ? {
          repository: filingRepository,
          groupCategory: filingGroupCategory,
          ...(filingGroupSubCategory?.trim() && { groupSubCategory: filingGroupSubCategory.trim() }),
        }
      : null;
  const { data: filingFolderTreeData } = useQuery({
    queryKey: ['edms', 'folders', 'tree', filingFolderTreeParams],
    queryFn: () => getFolderTree(filingFolderTreeParams || {}),
    enabled: !!filingFolderTreeParams,
  });
  const filingFoldersList = useMemo(
    () => (filingFolderTreeData ? flattenFolderTree(filingFolderTreeData) : []),
    [filingFolderTreeData]
  );

  const { data: selectedDocDetails } = useQuery({
    queryKey: ['edms', 'document', selectedDocId],
    queryFn: () => getDocument(selectedDocId),
    enabled: !!selectedDocId,
  });
  const selectedDoc = selectedDocDetails?.data ?? selectedDocDetails;

  const { data: viewUrlData } = useQuery({
    queryKey: ['documentViewUrl', selectedDocId],
    queryFn: () => getDocumentUrl(selectedDocId),
    enabled: !!selectedDocId,
    retry: false,
  });
  const viewUrl = useMemo(() => {
    if (!selectedDocId || !viewUrlData) return null;
    const d = viewUrlData;
    const raw =
      (typeof d === 'string' && d) ? d
      : d?.url ?? d?.viewUrl ?? d?.fileUrl
      ?? (typeof d?.data === 'string' ? d.data : null)
      ?? d?.data?.url ?? d?.data?.viewUrl ?? null;
    if (typeof raw === 'string' && raw.trim()) {
      const url = raw.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    const base =
      (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) ||
      (typeof window !== 'undefined' && window.location?.origin) ||
      '';
    return base ? `${base.replace(/\/$/, '')}/api/documents/${selectedDocId}/view` : null;
  }, [selectedDocId, viewUrlData]);

  useEffect(() => {
    if (selectedDoc) {
      setFilingTitle(selectedDoc.title ?? selectedDoc.name ?? selectedDoc.fileName ?? selectedDoc.originalName ?? '');
      setFilingDocumentTypeId(selectedDoc.documentTypeId ?? selectedDoc.documentType?._id ?? selectedDoc.documentType ?? '');
    }
  }, [selectedDoc]);

  const uploadMutation = useMutation({
    mutationFn: uploadDocumentTemporary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edms', 'documents', 'temporary'] });
      setUploadSuccess({ isOpen: true, message: 'Document uploaded. You can file it from the list.' });
    },
  });

  const fileMutation = useMutation({
    mutationFn: ({ id, payload }) => fileDocument(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edms', 'documents', 'temporary'] });
      setFileSuccess({ isOpen: true, message: 'Document filed successfully.' });
      setFilingError('');
      setSelectedDocId(null);
    },
    onError: (err) => {
      setFilingError(err?.response?.data?.message ?? err?.message ?? 'Failed to file document.');
    },
  });

  const handleUploadFiles = useCallback(
    (files) => {
      if (!files?.length) return;
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        if (filingTitle && i === 0) formData.append('title', filingTitle);
        if (filingDocumentTypeId) formData.append('documentTypeId', filingDocumentTypeId);
        uploadMutation.mutate(formData);
      }
    },
    [filingTitle, filingDocumentTypeId, uploadMutation]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setUploadDropActive(false);
      handleUploadFiles(e.dataTransfer?.files);
    },
    [handleUploadFiles]
  );
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadDropActive(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadDropActive(false);
  }, []);

  const handleFileSubmit = (e) => {
    e.preventDefault();
    setFilingError('');
    if (!selectedDocId) return;
    if (!filingFolderId) {
      setFilingError('Please select a parent folder to file the document.');
      return;
    }
    const payload = {
      folderId: filingFolderId,
      title: filingTitle.trim() || undefined,
      documentTypeId: filingDocumentTypeId || undefined,
    };
    fileMutation.mutate({ id: selectedDocId, payload });
  };

  const mimetype = selectedDoc?.mimetype ?? selectedDoc?.mimeType ?? selectedDoc?.contentType ?? '';

  return (
    <>
      <PageHeader
        title={`${APP_NAME} | ${MODULE}`}
        breadcrumbItems={[
          { label: APP_NAME, href: `/app/${APP_ID}` },
          { label: MODULE },
        ]}
      />
      <div className="rounded-2xl border border-cream/60 bg-white shadow-sm overflow-hidden flex flex-col min-h-[calc(100vh-10rem)]">
        <div className="flex flex-1 min-h-0">
          {/* Left column: 40% - document list (75%) + upload (25%) */}
          <div className="w-[40%] min-w-0 flex flex-col border-r border-cream/60">
            <div className="flex-[0.75] min-h-0 flex flex-col border-b border-cream/60">
              <div className="px-3 py-2 border-b border-cream/60 bg-slate-50/50">
                <h2 className="text-sm font-semibold text-slate-800">Documents for filing</h2>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {temporaryLoading && (
                  <p className="text-sm text-slate-500 p-4">Loading…</p>
                )}
                {!temporaryLoading && unfiledList.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                    <FileText className="h-10 w-10 text-slate-300 mb-2" aria-hidden />
                    <p className="text-sm">No documents for filing.</p>
                    <p className="text-xs mt-1">Upload files below or add them without a folder elsewhere.</p>
                  </div>
                )}
                {!temporaryLoading && unfiledList.length > 0 && (
                  <ul className="space-y-1">
                    {unfiledList.map((doc) => {
                      const id = doc._id ?? doc.id;
                      const name = doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? 'Untitled';
                      const isSelected = selectedDocId === id;
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            onClick={() => setSelectedDocId(id)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate flex items-center gap-2 ${
                              isSelected ? 'bg-primary/15 text-primary font-medium ring-1 ring-primary/30' : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <FileText className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                            {name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex-[0.25] min-h-0 flex flex-col p-2">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`flex-1 rounded-xl border-2 border-dashed flex flex-col items-center justify-center p-4 transition-colors ${
                  uploadDropActive ? 'border-primary bg-primary/5' : 'border-slate-300 bg-slate-50/50 hover:border-slate-400'
                }`}
              >
                <Upload className="h-8 w-8 text-slate-400 mb-2" aria-hidden />
                <p className="text-sm text-slate-600 text-center">Drag and drop files here</p>
                <p className="text-xs text-slate-500 mt-1">or</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleUploadFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                >
                  Browse to upload
                </button>
                <p className="text-xs text-slate-500 mt-2 text-center">Uploaded documents can be filed later (no folder required).</p>
              </div>
            </div>
          </div>

          {/* Right column: 60% - metadata (30%) + viewer (70%) */}
          <div className="flex-1 min-w-0 flex">
            <div className="w-[30%] min-w-0 flex flex-col border-r border-cream/60 bg-slate-50/30">
              <div className="px-3 py-2 border-b border-cream/60">
                <h2 className="text-sm font-semibold text-slate-800">Metadata &amp; filing</h2>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {!selectedDocId ? (
                  <p className="text-sm text-slate-500">Select a document from the list to view and file it.</p>
                ) : (
                  <form id="filing-form" onSubmit={handleFileSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={filingTitle}
                        onChange={(e) => setFilingTitle(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Document type</label>
                      <select
                        value={filingDocumentTypeId}
                        onChange={(e) => setFilingDocumentTypeId(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      >
                        <option value="">—</option>
                        {documentTypes.map((dt) => (
                          <option key={dt._id ?? dt.id} value={dt._id ?? dt.id}>
                            {dt.name ?? dt.code ?? dt._id}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="pt-2 border-t border-cream/60">
                      <p className="text-xs font-medium text-slate-600 mb-2">File to folder</p>
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-slate-500 mb-0.5">Repository</label>
                          <select
                            value={filingRepository}
                            onChange={(e) => {
                              setFilingRepository(e.target.value);
                              setFilingFolderId('');
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                          >
                            <option value="">Select</option>
                            {repositories?.map((r) => (
                              <option key={r._id ?? r.name} value={r.name ?? ''}>
                                {r.name ?? r.pathSegment ?? r._id}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-0.5">Group category</label>
                          <select
                            value={filingGroupCategory}
                            onChange={(e) => {
                              setFilingGroupCategory(e.target.value);
                              setFilingGroupSubCategory('');
                              setFilingFolderId('');
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                          >
                            <option value="">Select</option>
                            {GROUP_CATEGORY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        {filingGroupCategory === 'Personal' && (
                          <div>
                            <label className="block text-xs text-slate-500 mb-0.5">Under (Personal)</label>
                            <select
                              value={filingGroupSubCategory}
                              onChange={(e) => {
                                setFilingGroupSubCategory(e.target.value);
                                setFilingFolderId('');
                              }}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                            >
                              <option value="">Select</option>
                              {GROUP_SUBCATEGORY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs text-slate-500 mb-0.5">Parent folder</label>
                          <select
                            value={filingFolderId}
                            onChange={(e) => setFilingFolderId(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                            disabled={!filingRepository || !filingGroupCategory}
                          >
                            <option value="">Select folder</option>
                            {filingFoldersList.map((f) => (
                              <option key={f._id ?? f.id} value={f._id ?? f.id}>
                                {f.folderName ?? f.name ?? f.folderNumber ?? f._id}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    {filingError && (
                      <p className="text-sm text-red-600">{filingError}</p>
                    )}
                    <button
                      type="submit"
                      form="filing-form"
                      disabled={!filingFolderId || fileMutation.isPending}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FolderOpen className="h-4 w-4" aria-hidden />
                      {fileMutation.isPending ? 'Filing…' : 'File document'}
                    </button>
                  </form>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col bg-slate-100/50">
              <div className="px-3 py-2 border-b border-cream/60">
                <h2 className="text-sm font-semibold text-slate-800">Preview</h2>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                {!selectedDocId && (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <FileText className="h-16 w-16 text-slate-300" aria-hidden />
                    <p className="text-sm ml-2">Select a document to preview</p>
                  </div>
                )}
                {selectedDocId && !viewUrl && (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    <p className="text-sm">Loading preview…</p>
                  </div>
                )}
                {selectedDocId && viewUrl && (
                  <div className="h-full w-full flex flex-col">
                    {mimetype.startsWith('image/') ? (
                      <img
                        src={viewUrl}
                        alt={filingTitle || 'Document'}
                        className="max-h-full max-w-full object-contain mx-auto"
                      />
                    ) : (
                      <iframe
                        title="Document preview"
                        src={viewUrl}
                        className="w-full flex-1 min-h-0 border-0 bg-white"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SuccessDialog
        isOpen={uploadSuccess.isOpen}
        title="Success"
        message={uploadSuccess.message}
        onClose={() => setUploadSuccess({ isOpen: false, message: '' })}
        variant="success"
      />
      <SuccessDialog
        isOpen={fileSuccess.isOpen}
        title="Success"
        message={fileSuccess.message}
        onClose={() => setFileSuccess({ isOpen: false, message: '' })}
        variant="success"
      />
    </>
  );
};

export default EdmsFiling;
